/* Coming up — every call and live this person is invited to.
 *
 * The feed's row shows a handful; this is the whole list. What is on now
 * first, with Join or Watch; then everything later, grouped by day, each
 * with what it is, who it is for and who leads it. A call that has not
 * opened says when it will rather than offering a button that refuses.
 */
import { Link } from 'react-router-dom'
import { CalendarDays, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useMyCalls } from '@/api/queries'
import { cn } from '@/lib/utils'
import type { ScheduledCall } from '@/types/api'
import {
  KIND_ICON,
  KIND_NAME,
  OPENS_BEFORE_MS,
  isOn,
  isOpen,
  useJoinable,
} from './UpcomingCalls'

function dayKey(iso: string): string {
  const at = new Date(iso)
  const today = new Date()
  const tomorrow = new Date(today.getTime() + 86_400_000)
  if (at.toDateString() === today.toDateString()) return 'Today'
  if (at.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return at.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export function ComingUpPage() {
  const calls = useMyCalls()
  /* A meeting the church no longer offers cannot be joined, so it is not
     listed as something to come to. */
  const joinable = useJoinable()
  const rows = (calls.data ?? []).filter(joinable)
  const now = rows.filter(isOn)
  const later = rows.filter((c) => !isOn(c))

  const days: [string, ScheduledCall[]][] = []
  for (const c of later) {
    const key = dayKey(c.starts_at!)
    const last = days[days.length - 1]
    if (last && last[0] === key) last[1].push(c)
    else days.push([key, [c]])
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">Coming up</h1>
        <p className="text-base text-ink-2">
          Calls and lives you are invited to.
        </p>
      </header>

      {calls.isLoading ? <Card className="h-24 animate-pulse" /> : null}

      {!calls.isLoading && !rows.length ? (
        <Card className="p-s6 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
          <p className="mt-s2 font-semibold">Nothing arranged yet</p>
          <p className="mt-1 text-base text-ink-2">
            When your church arranges a call or goes live, it appears here and
            you are told.
          </p>
        </Card>
      ) : null}

      {now.length ? (
        <section className="space-y-s2">
          <h2 className="flex items-center gap-s2 text-sm font-bold uppercase tracking-wide text-danger">
            <span className="h-2 w-2 animate-pulse rounded-pill bg-danger" />
            Happening now
          </h2>
          {now.map((c) => (
            <Row key={c.id} call={c} />
          ))}
        </section>
      ) : null}

      {days.map(([day, list]) => (
        <section key={day} className="space-y-s2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink-2">{day}</h2>
          {list.map((c) => (
            <Row key={c.id} call={c} />
          ))}
        </section>
      ))}
    </div>
  )
}

function Row({ call }: { call: ScheduledCall }) {
  const Icon = KIND_ICON[call.kind]
  const on = isOn(call)
  const open = isOpen(call)
  const live = call.kind === 'live'
  const host = call.moderator_name ?? call.created_by_name

  let action: React.ReactNode = null
  if (live) {
    action = (
      <Button variant={on ? 'danger' : 'secondary'} size="sm" asChild>
        <Link to={`/calls/${call.id}/live`}>{on ? 'Watch' : 'Details'}</Link>
      </Button>
    )
  } else if (open) {
    action = (
      <Button variant={on ? 'danger' : 'primary'} size="sm" asChild>
        <Link to={`/calls/${call.id}/room`}>Join</Link>
      </Button>
    )
  } else {
    const opens = new Date(new Date(call.starts_at!).getTime() - OPENS_BEFORE_MS)
    action = (
      <span className="text-right text-xs font-semibold text-ink-3">
        Opens at
        <br />
        {opens.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
      </span>
    )
  }

  return (
    <Card className={cn('flex items-center gap-s3 p-s3', on && 'border-danger')}>
      <span
        className={cn(
          'grid h-12 w-12 shrink-0 place-items-center rounded-md',
          live ? 'bg-danger-soft text-danger' : 'bg-brand-softer text-brand',
        )}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-wide text-ink-2">
          {on ? (live ? 'Live now' : 'On now') : time(call.starts_at!)} · {KIND_NAME[call.kind]}
        </p>
        <p className="truncate font-bold">{call.title}</p>
        {call.description ? (
          <p className="line-clamp-2 text-base text-ink-2">{call.description}</p>
        ) : null}
        <p className="mt-1 flex flex-wrap items-center gap-x-s2 text-sm text-ink-3">
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" aria-hidden />
            {call.audience_label}
          </span>
          {host ? <span>· Led by {host}</span> : null}
          {call.recording ? <span>· Recorded</span> : null}
        </p>
      </div>
      <div className="shrink-0">{action}</div>
    </Card>
  )
}
